import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, CheckCircle2, Circle, Sparkles, ShieldCheck, ArrowLeft, RefreshCw,
  Zap, Gauge, ChevronRight, Lock, AlertTriangle,
} from "lucide-react";

const REASONS = [
  { key: "provider_refused_records", label: "Provider refused records" },
  { key: "client_unavailable", label: "Client unavailable" },
  { key: "strategic_legal_decision", label: "Strategic legal decision" },
  { key: "case_settled_early", label: "Case settled early" },
  { key: "records_unobtainable", label: "Records unobtainable" },
  { key: "other", label: "Other" },
];

function Gauge100({ score, grade, color }) {
  const r = 52, circ = 2 * Math.PI * r, off = circ - (score / 100) * circ;
  return (
    <div className="relative w-36 h-36" data-testid="cri-gauge">
      <svg className="w-36 h-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-heading text-4xl font-extrabold text-navy leading-none">{score}</div>
        <div className="text-xs text-slate-400 mt-1">CRI™</div>
        <div className="text-sm font-bold mt-0.5" style={{ color }}>{grade}</div>
      </div>
    </div>
  );
}

function LifecycleBar({ stages, score }) {
  return (
    <div className="flex items-center gap-1" data-testid="cri-lifecycle">
      {(stages || []).map((s) => {
        const active = score >= s.min && score <= s.max;
        const done = score > s.max;
        return (
          <div key={s.key} className="flex-1">
            <div className={`h-1.5 rounded-full ${done ? "bg-cobalt" : active ? "bg-cobalt/70" : "bg-slate-200"}`} />
            <div className={`mt-1 text-[10px] leading-tight ${active ? "text-cobalt font-semibold" : "text-slate-400"}`}>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function CriPanel() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [cri, setCri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showOverride, setShowOverride] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [notes, setNotes] = useState("");
  const [approval, setApproval] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/cri`); setCri(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const toggleTask = async (key, current) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/cases/${caseId}/cri/tasks/${key}`,
        { status: current === "complete" ? "pending" : "complete" });
      setCri({ ...data, case_number: cri.case_number, can_approve_demand: cri.can_approve_demand });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const recalc = async () => {
    setBusy("recalc");
    try { const { data } = await api.post(`/cases/${caseId}/cri/recalculate`); setCri({ ...data, case_number: cri.case_number, can_approve_demand: cri.can_approve_demand }); toast.success("CRI recalculated"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const submitOverride = async () => {
    try {
      const { data } = await api.post(`/cases/${caseId}/cri/demand-ready-override`,
        { reason_codes: reasons, notes, approval });
      setCri({ ...data, case_number: cri.case_number, can_approve_demand: cri.can_approve_demand });
      setShowOverride(false); setReasons([]); setNotes(""); setApproval(false);
      toast.success("Marked Demand Ready™");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>;
  if (!cri) return <p className="text-slate-500">CRI unavailable.</p>;

  const dr = cri.demand_ready || {};
  const ready = cri.status === "demand_ready";

  return (
    <div data-testid="cri-panel">
      <button onClick={() => navigate("/intelligence")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-cobalt mb-4"><ArrowLeft className="w-4 h-4" /> Back to Intelligence</button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-cobalt" /> Crash Ready Intelligence™</p>
          <h1 className="font-heading text-3xl font-extrabold text-navy mt-1">{cri.case_number}</h1>
          <p className="text-sm text-slate-400 mt-0.5">Template: {cri.template_name}</p>
        </div>
        <button onClick={recalc} disabled={busy === "recalc"} data-testid="cri-recalc" className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
          {busy === "recalc" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Recalculate
        </button>
      </div>

      {/* Top summary */}
      <div className="grid md:grid-cols-[auto,1fr] gap-6 items-center bg-white rounded-3xl border border-slate-100 p-6 mt-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex justify-center"><Gauge100 score={cri.score} grade={cri.grade} color={cri.grade_color} /></div>
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            {ready ? (
              <span data-testid="demand-ready-badge" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-50 text-green-600 font-semibold"><ShieldCheck className="w-4 h-4" /> 🟢 Demand Ready™{dr.manual ? " (Manual)" : ""}</span>
            ) : (
              <span data-testid="building-badge" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-amber-600 font-semibold"><Gauge className="w-4 h-4" /> 🟡 Building Toward Demand</span>
            )}
            <span className="text-sm text-slate-500">Stage: <b className="text-navy">{cri.stage?.label}</b></span>
          </div>
          <div className="mt-4"><LifecycleBar stages={cri.lifecycle_stages} score={cri.score} /></div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <span className="text-slate-500">Demand Readiness Confidence™: <b style={{ color: cri.grade_color }}>{dr.confidence}%</b></span>
            <span className="text-slate-500">Missing required: <b className={cri.missing_required?.length ? "text-amber-600" : "text-green-600"}>{cri.missing_required?.length || 0}</b></span>
          </div>
          {dr.confidence_note && <p className="text-xs text-slate-400 mt-1">{dr.confidence_note}</p>}
        </div>
      </div>

      {/* Demand Ready manual approval */}
      {cri.can_approve_demand && !dr.manual && (
        <button onClick={() => setShowOverride(true)} data-testid="open-demand-override" className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-medium hover:bg-navy/90">
          <ShieldCheck className="w-4 h-4" /> Mark Demand Ready™
        </button>
      )}
      {dr.manual && (
        <div className="mt-4 rounded-2xl border border-green-100 bg-green-50/50 p-4 text-sm" data-testid="override-summary">
          <div className="font-semibold text-navy">Manually approved by {dr.approved_by_name} ({(dr.approved_role || "").replace("_", " ")})</div>
          <div className="text-slate-500 mt-1">Reasons: {(dr.reason_codes || []).map((r) => r.replace(/_/g, " ")).join(", ")}{dr.notes ? ` · ${dr.notes}` : ""}</div>
          {dr.missing_items_at_approval?.length > 0 && <div className="text-amber-600 mt-1">{dr.missing_items_at_approval.length} required item(s) were incomplete at approval.</div>}
        </div>
      )}

      {/* Next Best Actions */}
      {cri.next_best_actions?.length > 0 && (
        <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6" data-testid="next-best-actions">
          <h3 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-cobalt" /> Next Best Actions</h3>
          <div className="space-y-2">
            {cri.next_best_actions.map((a) => (
              <div key={a.key} className="flex items-center justify-between rounded-2xl border border-slate-100 p-3">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.priority === "High" ? "bg-red-50 text-red-500" : a.priority === "Medium" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{a.priority}</span>
                  <span className="text-sm text-navy font-medium">{a.recommended_action}</span>
                </div>
                <span className="text-xs text-cobalt font-semibold">+{a.estimated_cri_gain} CRI</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="mt-6 space-y-4">
        {cri.categories.map((cat) => (
          <div key={cat.key} className="bg-white rounded-3xl border border-slate-100 p-6" data-testid={`cri-category-${cat.key}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-navy">{cat.label}</h3>
              <span className="text-xs text-slate-400">{cat.completion_pct}% complete</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {cat.tasks.map((t) => {
                const done = t.status === "complete";
                return (
                  <button key={t.key} onClick={() => toggleTask(t.key, t.status)} disabled={busy === t.key}
                    data-testid={`cri-task-${t.key}`}
                    className={`flex items-center gap-2 text-left px-3 py-2.5 rounded-2xl border transition-all ${done ? "border-green-200 bg-green-50/40" : "border-slate-100 hover:border-cobalt/40"}`}>
                    {busy === t.key ? <Loader2 className="w-5 h-5 text-cobalt animate-spin shrink-0" /> :
                      done ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                    <span className="flex-1">
                      <span className={`text-sm ${done ? "text-navy font-medium" : "text-slate-600"}`}>{t.label}</span>
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">{t.requirement}</span>
                      {t.source === "auto" && done && <span className="ml-1 text-[10px] text-cobalt">· auto</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Override modal */}
      {showOverride && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="demand-override-modal">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <h3 className="font-heading text-xl font-extrabold text-navy flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-cobalt" /> Mark Demand Ready™</h3>
            {cri.missing_required?.length > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{cri.missing_required.length} required item(s) still incomplete. This override and the missing items will be permanently audited.</span>
              </div>
            )}
            <p className="text-sm text-slate-500 mt-3 font-medium">Reason</p>
            <div className="mt-2 space-y-1.5">
              {REASONS.map((r) => (
                <label key={r.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid={`reason-${r.key}`} checked={reasons.includes(r.key)}
                    onChange={(e) => setReasons(e.target.checked ? [...reasons, r.key] : reasons.filter((x) => x !== r.key))} />
                  {r.label}
                </label>
              ))}
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="override-notes" placeholder="Notes" className="mt-3 w-full border border-slate-200 rounded-2xl px-3 py-2 text-sm outline-none focus:border-cobalt" rows={2} />
            <label className="flex items-center gap-2 text-sm text-navy mt-3 cursor-pointer">
              <input type="checkbox" checked={approval} onChange={(e) => setApproval(e.target.checked)} data-testid="override-approval" />
              Attorney Approval — I authorize marking this case Demand Ready.
            </label>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowOverride(false)} className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">Cancel</button>
              <button onClick={submitOverride} disabled={!approval || reasons.length === 0} data-testid="confirm-override" className="flex-1 px-4 py-2.5 rounded-full bg-navy text-white text-sm font-medium disabled:opacity-40">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
